import { joinDateWithTimeSlot } from 'common/components/entities/Calendar/utils'
import { getCountryVat } from 'common/constants/countryVatList'
import EntityTypeEnum from 'common/enums/entityTypeEnum'
import { FieldSlugEnum } from 'common/enums/FieldSlugEnum'
import {
  PaymentMethodEnum,
  paymentMethodsWithPersonalTaxNumber,
} from 'common/enums/PaymentMethodEnum'
import {
  BadRequest,
  InternalError,
  NetworkError,
  ServiceUnavailable,
} from 'common/errors'
import { BookingPaidCalendarInterface } from 'common/types/entities/booking-paid-calendar-interface'
import { OldEntityInterface } from 'common/types/entities/OldEntityInterface'
import { TwoStepPaymentFormStepInterface } from 'common/types/entities/two-step-payment-form-interface'
import { EntitiesStructure } from 'common/types/Page'
import { optInFail } from 'publisher/actions/optInActions'
import useOptIn, { selectors as optInSelectors } from 'publisher/hooks/useOptIn'
import { useRecaptchaValidation } from 'publisher/hooks/useRecaptchaValidation'
import { getEntityParents } from 'publisher/reducers/pageReducer'
import {
  managementSelectors,
  typedPageSelectors,
  useManagement,
  usePage,
  usePayment,
  useOptIn as useTypedOptIn,
} from 'publisher/store'
import { getFieldValueBySlug } from 'publisher/store/optIn/optInSelectors'
import pageSelectors from 'publisher/store/page/pageSelectors'
import paymentSelectors from 'publisher/store/payment/paymentSelectors'
import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { PaymentButtonEntityInterface } from 'src/shared/entities/payment-button/payment-button.schema'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'

export type PaymentRequestPayload = {
  customer: {
    fields: Record<FieldSlugEnum, string> // add type after refactoring opt-in
    popupId: string | null // will be removed
    isDesktop: boolean // will be removed
    // locale: string | null
  }
  paymentMethod: string
  checkboxIds: string[]
  offerPricePlan?: number
  orderBumpOffers: number[]
  coupon?: number
  product?: {
    id?: number
    variant?: string
    quantity?: number
    shippingCondition?: number | null
    shippingRule?: number | null
  }
  popupId: string | null
  isDesktop: boolean
  captcha?: string
}

type PaymentHandlerType<T> = (body: PaymentRequestPayload) => Promise<T>

export default function usePaymentSubmit(
  entity:
    | OldEntityInterface
    | PaymentButtonEntityInterface
    | BookingPaidCalendarInterface
    | TwoStepPaymentFormStepInterface,
) {
  const { t } = useTypedTranslation()
  const dispatch = useDispatch()
  // const locale = usePage(typedPageSelectors.getLocale)
  const [isLoading, setLoading] = useState(false)
  const fields = useOptIn(optInSelectors.getFields)
  const bookingModel = useOptIn(optInSelectors.getPaidBookingOptInFields)
  const entityParents: EntitiesStructure = usePage(
    page => getEntityParents(page.entities, entity) as EntitiesStructure,
  )
  const isBookingPaidCalendar = Object.values(entityParents)?.some(
    entity => entity.type === EntityTypeEnum.BookingPaidCalendar,
  )
  const [errors, setErrors] = useState<string[]>([])
  const pricePlans = usePayment(paymentSelectors.getPricePlans)
  const checkedPricePlanId = usePayment(paymentSelectors.getCheckedPricePlanId)
  const coupon = usePayment(paymentSelectors.getCheckedCoupon)
  const checkedBumpIds = usePayment(paymentSelectors.getCheckedBumpIds)
  const paymentMethod = usePayment(paymentSelectors.getActivePaymentMethod)
  const isCustomerPersonal = usePayment(paymentSelectors.isCustomerPersonal)
  const popupId = usePage(p => pageSelectors.getAscendantPopupId(p, entity))
  const isDesktop = useManagement(managementSelectors.isDesktop)
  const confirmedCheckboxesIds = useManagement(
    managementSelectors.getConfirmedCheckboxesIds,
  )
  const product = usePayment(paymentSelectors.getProduct)
  const productQuantity = usePayment(paymentSelectors.getProductQuantity)
  const activeProductVariant = usePayment(
    paymentSelectors.getProductActiveVariant,
  )
  const selectedShipping = usePayment(paymentSelectors.getSelectedShipping)
  const mandatoryCheckboxes = usePage(p =>
    pageSelectors.getVisibleMandatoryCheckboxes(p, isDesktop, popupId),
  )
  const customerCountryCode = useTypedOptIn(optIn =>
    getFieldValueBySlug(optIn, FieldSlugEnum.Country),
  )
  const isTaxFieldIsDisabled = getCountryVat(customerCountryCode) === null

  const { validateRecaptchas } = useRecaptchaValidation(entity)

  const handlePayment = async <T>(
    paymentMethodHandler: PaymentHandlerType<T>,
    onError?: (
      errors: string[] | unknown,
      requestBody?: Record<string, unknown>,
    ) => void,
  ) => {
    setErrors([])
    const arePricePlansEmpty = pricePlans.length === 0

    if (arePricePlansEmpty && !product?.id) {
      setErrors([
        ...new Set(errors.concat([t('validation.empty_price_plans')])),
      ])
      if (onError) {
        onError(errors)
      }
      return
    }

    if (!paymentMethod) {
      setErrors([
        ...new Set(errors.concat([t('validation.empty_payment_methods')])),
      ])
      if (onError) {
        onError(errors)
      }
      return
    }

    const checkboxesErrors = mandatoryCheckboxes.reduce((errors, checkbox) => {
      if (!confirmedCheckboxesIds.includes(checkbox.id)) {
        errors.push(checkbox.errorText)
      }
      return errors
    }, [] as string[])

    if (checkboxesErrors.length > 0) {
      setErrors(checkboxesErrors)
      if (onError) {
        onError(checkboxesErrors)
      }
      return
    }

    const { recaptchaToken, error: recaptchaError } = validateRecaptchas()

    if (recaptchaError) {
      setErrors([t('core.errors.something_went_wrong')])
      if (onError) {
        onError(errors)
      }
      return
    }

    if (isCustomerPersonal) {
      delete fields[FieldSlugEnum.CompanyName]
      if (
        !paymentMethodsWithPersonalTaxNumber.includes(
          paymentMethod as PaymentMethodEnum,
        )
      ) {
        delete fields[FieldSlugEnum.TaxNumber]
      }
    }
    if (
      !paymentMethodsWithPersonalTaxNumber.includes(
        paymentMethod as PaymentMethodEnum,
      ) &&
      isTaxFieldIsDisabled
    ) {
      delete fields[FieldSlugEnum.TaxNumber]
    }
    // todo we need to know why can't validate company name on a client side
    // const emptyFieldErrors: Record<string, string[]> = validateEmptyOptInFields(
    //   visibleOptInFields,
    //   t,
    // )
    // if (Object.keys(emptyFieldErrors).length) {
    //   dispatch(optInFail({ fields: emptyFieldErrors }))
    //   return
    // }
    const calculatedFormProperties = product
      ? {
          product: {
            id: product?.id,
            variant: activeProductVariant?.id,
            quantity: productQuantity,
            shippingCondition: selectedShipping?.conditionId,
            shippingRule: selectedShipping?.ruleId,
          },
          orderBumpOffers: checkedBumpIds,
          ...(coupon ? { coupon: coupon.id } : {}),
        }
      : {
          offerPricePlan: checkedPricePlanId,
          orderBumpOffers: checkedBumpIds,
          ...(coupon ? { coupon: coupon.id } : {}),
        }
    setLoading(true)

    const body: PaymentRequestPayload = {
      customer: {
        fields,
        popupId,
        isDesktop,
        // locale,
        ...(mandatoryCheckboxes.length ? { purchaseTermsAccepted: true } : {}),
      },
      ...(isBookingPaidCalendar
        ? {
            bookingModel: {
              eventId: bookingModel.eventId,
              ownerId: bookingModel.ownerId,
              eventLocationId: bookingModel.eventLocationId,
              timeSlot: joinDateWithTimeSlot(
                bookingModel.date,
                bookingModel.timeSlot,
              ),
            },
            timeZone: bookingModel.timeZone,
          }
        : {}),
      checkboxIds: confirmedCheckboxesIds,
      popupId,
      isDesktop,
      paymentMethod,
      ...calculatedFormProperties,
      captcha: recaptchaToken,
    }
    try {
      return await paymentMethodHandler(body)
    } catch (error) {
      if (error instanceof BadRequest) {
        setErrors(error.response.data.errors.common)
        dispatch(optInFail({ fields: error.response.data.errors.fields }))
      } else if (error instanceof ServiceUnavailable) {
        setErrors([t('core.errors.service_temporary_unavailable')])
      } else if (error instanceof NetworkError) {
        setErrors([t('core.errors.no_connection')])
      } else if (error instanceof InternalError) {
        setErrors([t('core.error.title')])
      }
      if (onError) {
        onError(error, body)
      }
    } finally {
      setLoading(false)
    }
  }

  return {
    errors,
    setErrors,
    isLoading,
    setLoading,
    submit: handlePayment,
    fields,
  }
}
